/**
 * components/feedback/Feedback.jsx — App-wide toasts and confirm dialogs.
 *
 * Replaces browser alert()/window.confirm() with branded UI. Mount
 * <FeedbackProvider> once (App.jsx), then call from anywhere — no hook needed:
 *
 *   import { toast, confirmDialog } from '../../components/feedback/Feedback';
 *   toast.success('Saved');
 *   toast.error('Could not save: ' + msg);
 *   if (!(await confirmDialog({ title: 'Delete invoice?', message: '…', confirmText: 'Delete', danger: true }))) return;
 *
 * If the provider is not mounted (e.g. an isolated test render), both fall
 * back to the native browser dialogs so nothing is silently swallowed.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';

const NAVY = '#22324E';
const ORANGE = '#C9721C';
const RED = '#B42318';
const GREEN = '#15803D';

let pushToast = null;
let openConfirm = null;

function show(kind, message, opts = {}) {
    const text = message == null ? '' : String(message);
    if (pushToast) return pushToast({ kind, message: text, ...opts });
    window.alert(text); // eslint-disable-line no-alert
}

export const toast = {
    success: (m, o) => show('success', m, o),
    error: (m, o) => show('error', m, o),
    info: (m, o) => show('info', m, o),
    warning: (m, o) => show('warning', m, o),
};

/**
 * Promise-based confirm. Accepts a string or { title, message, confirmText, cancelText, danger }.
 * Resolves true when the user confirms, false otherwise.
 */
export function confirmDialog(opts) {
    const o = typeof opts === 'string' ? fromString(opts) : (opts || {});
    if (openConfirm) return openConfirm(o);
    return Promise.resolve(window.confirm([o.title, o.message].filter(Boolean).join('\n\n'))); // eslint-disable-line no-alert
}

// Plain-string confirms (drop-in for window.confirm): the first question
// becomes the title, the rest the body; destructive wording gets a red button.
function fromString(text) {
    const q = text.indexOf('?');
    const split = q > 0 && q < 120;
    const title = split ? text.slice(0, q + 1) :'Are you sure?';
    const message = (split ? text.slice(q + 1) : text).trim();
    const verb = (text.match(/\b(delete|remove|disconnect|void|discard|revoke|reject|regenerate)\b/i) || [])[1];
    return {
        title,
        message,
        danger: !!verb,
        confirmText: verb ? verb[0].toUpperCase() + verb.slice(1).toLowerCase() : 'Confirm',
    };
}

const ACCENT = { success: GREEN, error: RED, info: NAVY, warning: ORANGE };
const ICON = { success: '✓', error: '!', info: 'i', warning: '!' };

export function FeedbackProvider({ children }) {
    const [toasts, setToasts] = useState([]);
    const [dialog, setDialog] = useState(null);
    const idRef = useRef(0);

    const dismiss = useCallback(id => setToasts(t => t.filter(x => x.id !== id)), []);

    useEffect(() => {
        pushToast = ({ kind, message, duration }) => {
            const id = ++idRef.current;
            // Errors and long messages stay up longer so they can be read.
            const ms = duration ?? (kind === 'error' ? 8000 : Math.min(9000, 3500 + message.length * 30));
            setToasts(t => [...t.slice(-4), { id, kind, message }]);
            setTimeout(() => dismiss(id), ms);
            return id;
        };
        openConfirm = o => new Promise(resolve => setDialog({ ...o, resolve }));
        return () => { pushToast = null; openConfirm = null; };
    }, [dismiss]);

    const close = result => {
        if (dialog) dialog.resolve(result);
        setDialog(null);
    };

    return (
        <>
            {children}

            <div
                aria-live="polite"
                style={{ position: 'fixed', top: 16, right: 16, left: 16, zIndex: 100000, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8, pointerEvents: 'none' }}
            >
                {toasts.map(t => (
                    <div
                        key={t.id}
                        role={t.kind === 'error' ? 'alert' : 'status'}
                        style={{
                            pointerEvents: 'auto', maxWidth: 420, width: '100%', background: '#fff', color: NAVY,
                            borderLeft: `4px solid ${ACCENT[t.kind]}`, borderRadius: 10,
                            boxShadow: '0 10px 30px rgba(34,50,78,0.18)', padding: '12px 14px',
                            display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 14, lineHeight: 1.4,
                            fontFamily: 'inherit',
                        }}
                    >
                        <span style={{ flex: '0 0 22px', height: 22, borderRadius: '50%', background: ACCENT[t.kind], color: '#fff', fontWeight: 700, fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {ICON[t.kind]}
                        </span>
                        <span style={{ flex: 1, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{t.message}</span>
                        <button
                            onClick={() => dismiss(t.id)}
                            aria-label="Dismiss"
                            style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: 18, lineHeight: 1, padding: 0 }}
                        >×</button>
                    </div>
                ))}
            </div>

            {dialog && (
                <div
                    onMouseDown={e => { if (e.target === e.currentTarget) close(false); }}
                    style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 100001, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
                >
                    <ConfirmBox dialog={dialog} onClose={close} />
                </div>
            )}
        </>
    );
}

function ConfirmBox({ dialog, onClose }) {
    const confirmRef = useRef(null);
    useEffect(() => {
        confirmRef.current?.focus();
        const onKey = e => { if (e.key === 'Escape') onClose(false); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);

    const accent = dialog.danger ? RED : ORANGE;
    return (
        <div role="dialog" aria-modal="true" style={{ background: '#fff', borderRadius: 14, maxWidth: 440, width: '100%', boxShadow: '0 25px 50px rgba(0,0,0,0.25)', overflow: 'hidden', fontFamily: 'inherit' }}>
            <div style={{ height: 4, background: accent }} />
            <div style={{ padding: '20px 22px 8px' }}>
                <h3 style={{ margin: '0 0 8px', fontSize: 18, fontWeight: 700, color: NAVY }}>
                    {dialog.title || 'Are you sure?'}
                </h3>
                {dialog.message && (
                    <p style={{ margin: 0, fontSize: 14, color: '#475569', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{dialog.message}</p>
                )}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, padding: '16px 22px 20px' }}>
                <button
                    onClick={() => onClose(false)}
                    style={{ padding: '9px 16px', borderRadius: 8, border: '1px solid #cbd5e1', background: '#fff', color: NAVY, fontWeight: 600, cursor: 'pointer', fontSize: 14 }}
                >
                    {dialog.cancelText || 'Cancel'}
                </button>
                <button
                    ref={confirmRef}
                    onClick={() => onClose(true)}
                    style={{ padding: '9px 16px', borderRadius: 8, border: 'none', background: dialog.danger ? RED : NAVY, color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: 14 }}
                >
                    {dialog.confirmText || 'Confirm'}
                </button>
            </div>
        </div>
    );
}
